import { useState } from 'react'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function VendorPartnership() {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    businessType: '',
    description: ''
  })

  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const benefits = [
    {
      icon: 'ri-group-line',
      title: 'Access to a growing online customer network',
      description: 'Reach more customers through our expanding digital platform',
      color: 'from-orange-400 to-orange-600'
    },
    {
      icon: 'ri-store-2-line',
      title: 'Structured order management system',
      description: 'Efficiently handle and track all your orders in one place',
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: 'ri-time-line',
      title: 'Professional delivery support',
      description: 'Reliable delivery network to serve your customers',
      color: 'from-pink-400 to-pink-600'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Increased brand visibility',
      description: 'Grow your brand presence on our platform',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Reliable payment processing',
      description: 'Secure and timely payment processing for all transactions',
      color: 'from-purple-400 to-purple-600'
    }
  ]

  const standards = [
    'Food Quality',
    'Hygiene and Safety',
    'Service Consistency',
    'Timely Preparation'
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError('')

    try {
      const response = await fetch('https://formspree.io/f/mkoabzka', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: formData.businessName,
          ownerName: formData.ownerName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          businessType: formData.businessType,
          description: formData.description
        })
      })

      if (response.ok) {
        setSubmitted(true)
        setFormData({
          businessName: '',
          ownerName: '',
          email: '',
          phone: '',
          address: '',
          businessType: '',
          description: ''
        })
      } else {
        setSubmitError('Something went wrong. Please try again or contact us at info@amibites.com')
      }
    } catch (err) {
      console.error('Submission error:', err)
      setSubmitError('Something went wrong. Please try again or contact us at info@amibites.com')
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="pt-20">
      <SEO 
        title="Partner With Us - Restaurant & Vendor Partnership Program | Ami Bites"
        description="Join Ami Bites vendor network. Grow your restaurant business with our online food delivery platform. Access new customers, professional delivery support & reliable payment processing in Zimbabwe."
        keywords="restaurant partnership Zimbabwe, food vendor partnership, delivery platform for restaurants, restaurant online ordering, vendor registration Zimbabwe, food business partnership, restaurant delivery service"
        canonical="https://amibites.com/partner-with-us"
      />
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why <span className="text-gradient">Partner</span> With Us?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ami Bites provides restaurants and food businesses with a reliable digital channel to expand their customer base and increase revenue.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all group"
              >
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl mb-6`}
                >
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </motion.div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Our <span className="text-gradient">Vendor Standards</span>
              </h2>
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                We partner with food providers who meet professional standards in:
              </p>
              <div className="space-y-4">
                {standards.map((standard, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3 p-4 bg-gradient-to-br from-orange-50 to-green-50 rounded-xl"
                  >
                    <i className="ri-checkbox-circle-line text-2xl text-green-600 flex-shrink-0"></i>
                    <span className="text-gray-700 font-medium">{standard}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-green-400 rounded-3xl opacity-20 blur-2xl"></div>
              <img
                src="/logo.png"
                alt="Partner with us"
                className="relative rounded-3xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Become a Vendor</span>
            </h2>
            <p className="text-xl text-gray-600">
              Submit your business details and our onboarding team will contact you for verification and integration.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-2xl p-8"
          >
            {submitted ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-12"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <i className="ri-checkbox-circle-line text-4xl text-white"></i>
                </motion.div>
                <h3 className="text-3xl font-bold mb-4 text-gradient-green">Application Submitted!</h3>
                <p className="text-gray-600 text-lg">
                  Thank you for your interest. Our team will review your application and contact you within 2-3 business days.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      required
                      value={formData.businessName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="Enter your business name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Owner Name *
                    </label>
                    <input
                      type="text"
                      name="ownerName"
                      required
                      value={formData.ownerName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="Enter owner's name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="+263 XXX XXX XXX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="Full business address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Type *
                  </label>
                  <select
                    name="businessType"
                    required
                    value={formData.businessType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                  >
                    <option value="">Select business type</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="fast-food">Fast Food</option>
                    <option value="cafe">Café</option>
                    <option value="bakery">Bakery</option>
                    <option value="home-kitchen">Home-based Kitchen</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Description *
                  </label>
                  <textarea
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your business, cuisine type, and what makes you special..."
                  ></textarea>
                </div>

                {submitError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {submitError}
                  </div>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <span>{submitting ? 'Submitting...' : 'Submit Application'}</span>
                  {!submitting && <i className="ri-arrow-right-line text-xl"></i>}
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  )
}
